import { currencyService } from './currencyService';

export type OrderPaymentStatus =
  | 'draft'
  | 'awaiting_payment'
  | 'partially_paid'
  | 'paid'
  | 'refunded'
  | 'canceled';

export type OrderFulfillmentStatus =
  | 'new'
  | 'scheduled'
  | 'in_progress'
  | 'delivered'
  | 'completed'
  | 'on_hold'
  | 'canceled';

export type OrderSource = 'manual' | 'payment_link' | 'checkout';
export type OrderDeliveryMode = 'digital' | 'service' | 'subscription' | 'project';

export interface AdminOrderLineItem {
  id: string;
  serviceId?: string;
  label: string;
  quantity: number;
  unitAmount: number;
  lineTotal: number;
  currencyCode: string;
}

export interface AdminOrder {
  id: string;
  orderNumber: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientCompany: string;
  serviceId: string;
  serviceName: string;
  amount: number;
  lineItems: AdminOrderLineItem[];
  currencyCode: string;
  paymentStatus: OrderPaymentStatus;
  fulfillmentStatus: OrderFulfillmentStatus;
  source: OrderSource;
  deliveryMode: OrderDeliveryMode;
  orderedAt: string;
  paidAt: string;
  deliveryDate: string;
  trackingReference: string;
  quoteNumber: string;
  paymentLinkKey: string;
  notes: string;
  internalNotes: string;
  lastUpdateAt: string;
}

const ENDPOINT = '/orderManagement.php';

let ordersCache: AdminOrder[] | null = null;

const parseResponse = async <T,>(response: Response): Promise<T> => {
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || 'Erreur gestion commandes');
  }

  return payload as T;
};

const generateRecordId = (prefix: string) => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}_${crypto.randomUUID()}`;
  }

  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const padSequence = (value: number) => String(value).padStart(4, '0');

const generateOrderNumber = () => {
  const now = new Date();
  const year = now.getFullYear();
  const sequence = Number(String(now.getTime()).slice(-4));
  return `CMD-${year}-${padSequence(sequence)}`;
};

const normalizeDate = (value?: string | null, fallback = '') => {
  if (!value) {
    return fallback;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return fallback;
  }

  return parsed.toISOString();
};

const sortOrders = (orders: AdminOrder[]) =>
  [...orders].sort(
    (left, right) =>
      new Date(right.orderedAt || right.lastUpdateAt).getTime() -
      new Date(left.orderedAt || left.lastUpdateAt).getTime()
  );

const normalizeLineItems = (
  rawLineItems: unknown,
  currencyCode: string,
  fallbackLabel: string,
  fallbackAmount: number
): AdminOrderLineItem[] => {
  const source = Array.isArray(rawLineItems) ? rawLineItems : [];

  const normalized = source
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        return null;
      }

      const candidate = entry as Record<string, unknown>;
      const label = String(
        candidate.label ?? candidate.name ?? candidate.serviceName ?? ''
      ).trim();
      const quantityRaw = Number(candidate.quantity ?? 1);
      const quantity =
        Number.isFinite(quantityRaw) && Math.trunc(quantityRaw) > 0
          ? Math.trunc(quantityRaw)
          : 1;
      const unitAmount = Number(candidate.unitAmount ?? candidate.amount ?? 0);
      const lineTotalRaw = Number(candidate.lineTotal);
      const lineTotal = lineTotalRaw > 0 ? lineTotalRaw : unitAmount * quantity;

      if (!label || !Number.isFinite(lineTotal) || lineTotal <= 0) {
        return null;
      }

      const normalizedLineItem: AdminOrderLineItem = {
        id: String(candidate.id ?? `item_${index + 1}`),
        label,
        quantity,
        unitAmount: Number.isFinite(unitAmount) ? unitAmount : 0,
        lineTotal,
        currencyCode: String(candidate.currencyCode ?? currencyCode ?? 'EUR').toUpperCase(),
      };
      const serviceId = String(candidate.serviceId ?? '').trim();
      if (serviceId) {
        normalizedLineItem.serviceId = serviceId;
      }

      return normalizedLineItem;
    })
    .filter((entry): entry is AdminOrderLineItem => Boolean(entry));

  if (normalized.length > 0) {
    return normalized;
  }

  if (fallbackAmount > 0) {
    return [
      {
        id: 'item_1',
        label: fallbackLabel || 'Service',
        quantity: 1,
        unitAmount: fallbackAmount,
        lineTotal: fallbackAmount,
        currencyCode: (currencyCode || 'EUR').toUpperCase(),
      },
    ];
  }

  return [];
};

const sanitizeOrder = (order: AdminOrder): AdminOrder => {
  const currencyCode = order.currencyCode?.trim().toUpperCase() || 'EUR';
  const normalizedLineItems = normalizeLineItems(
    order.lineItems as unknown,
    currencyCode,
    order.serviceName,
    Number(order.amount || 0)
  );
  const amountFromLineItems = Number(
    normalizedLineItems.reduce((total, lineItem) => total + lineItem.lineTotal, 0).toFixed(2)
  );
  const primaryLineItem = normalizedLineItems.find(
    (lineItem) => lineItem.label.trim().length > 0
  );
  const fallbackAmount = Number(order.amount || 0);

  return {
    lineItems: normalizedLineItems,
    id: order.id || generateRecordId('order'),
    orderNumber: order.orderNumber?.trim() || generateOrderNumber(),
    clientId: order.clientId?.trim() || '',
    clientName: order.clientName?.trim() || '',
    clientEmail: order.clientEmail?.trim().toLowerCase() || '',
    clientPhone: order.clientPhone?.trim() || '',
    clientCompany: order.clientCompany?.trim() || '',
    serviceId: order.serviceId?.trim() || primaryLineItem?.serviceId || '',
    serviceName: order.serviceName?.trim() || primaryLineItem?.label || '',
    amount: amountFromLineItems > 0 ? amountFromLineItems : fallbackAmount,
    currencyCode,
    paymentStatus: order.paymentStatus || 'draft',
    fulfillmentStatus: order.fulfillmentStatus || 'new',
    source: order.source || 'manual',
    deliveryMode: order.deliveryMode || 'service',
    orderedAt: normalizeDate(order.orderedAt, new Date().toISOString()),
    paidAt: normalizeDate(order.paidAt),
    deliveryDate: normalizeDate(order.deliveryDate),
    trackingReference: order.trackingReference?.trim() || '',
    quoteNumber: order.quoteNumber?.trim() || '',
    paymentLinkKey: order.paymentLinkKey?.trim() || '',
    notes: order.notes?.trim() || '',
    internalNotes: order.internalNotes?.trim() || '',
    lastUpdateAt: new Date().toISOString(),
  };
};

const readStore = async (forceRefresh = false): Promise<AdminOrder[]> => {
  if (!forceRefresh && ordersCache !== null) {
    return ordersCache;
  }

  const response = await fetch(ENDPOINT, {
    credentials: 'include',
    cache: 'no-store',
  });

  const payload = await parseResponse<{ orders: AdminOrder[] }>(response);
  ordersCache = sortOrders(
    Array.isArray(payload.orders)
      ? payload.orders.map((order) => sanitizeOrder(order))
      : []
  );

  return ordersCache;
};

const writeStore = async (orders: AdminOrder[]) => {
  const response = await fetch(ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      orders: orders.map((order) => sanitizeOrder(order)),
    }),
  });

  const payload = await parseResponse<{ ok: true; orders: AdminOrder[] }>(response);
  ordersCache = sortOrders(
    Array.isArray(payload.orders)
      ? payload.orders.map((order) => sanitizeOrder(order))
      : []
  );

  return ordersCache;
};

export const createEmptyOrder = (): AdminOrder => ({
  id: generateRecordId('order'),
  orderNumber: generateOrderNumber(),
  clientId: '',
  clientName: '',
  clientEmail: '',
  clientPhone: '',
  clientCompany: '',
  serviceId: '',
  serviceName: '',
  amount: 0,
  lineItems: [],
  currencyCode: currencyService.getDisplayCurrencyCode(),
  paymentStatus: 'draft',
  fulfillmentStatus: 'new',
  source: 'manual',
  deliveryMode: 'service',
  orderedAt: new Date().toISOString(),
  paidAt: '',
  deliveryDate: '',
  trackingReference: '',
  quoteNumber: '',
  paymentLinkKey: '',
  notes: '',
  internalNotes: '',
  lastUpdateAt: new Date().toISOString(),
});

export async function getOrders(forceRefresh = false): Promise<AdminOrder[]> {
  return readStore(forceRefresh);
}

export async function saveOrder(order: AdminOrder): Promise<AdminOrder> {
  const sanitized = sanitizeOrder(order);

  if (!sanitized.orderNumber || !sanitized.clientName || !sanitized.clientEmail) {
    throw new Error('Numero de commande, client et email sont obligatoires.');
  }

  const orders = await readStore(true);
  const nextOrders = orders.some((entry) => entry.id === sanitized.id)
    ? orders.map((entry) => (entry.id === sanitized.id ? sanitized : entry))
    : [sanitized, ...orders];

  await writeStore(nextOrders);
  return sanitized;
}

export async function removeOrder(orderId: string): Promise<void> {
  const orders = await readStore(true);
  const nextOrders = orders.filter((order) => order.id !== orderId);
  await writeStore(nextOrders);
}

export const adminOrderService = {
  getOrders,
  saveOrder,
  removeOrder,
  createEmptyOrder,
};
